/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { colors, DataTable, Heading, HFlow, Icon, TableFooter, Text, Tooltip, VFlow } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { Ellipsis } from 'components/Ellipsis'
import { InfoIcon } from 'components/InfoIcon'
import { HideOnScreenSize } from 'components/responsive'
import { TableBox, usePagedTableProps } from 'components/table'
import { isSameDay, toDate } from 'date-fns'
import {
  ListaRegulacaoQuery,
  PrioridadeRegulacaoEnum,
  StatusRegulacaoEnum,
  TipoRegulacaoEnum,
} from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { ReactComponent as CalendarCheckOutline } from 'images/regulacao/calendar-check-outline.svg'
import { ReactComponent as CalendarClockOutline } from 'images/regulacao/calendar-clock-outline.svg'
import { ReactComponent as CalendarTimesOutline } from 'images/regulacao/calendar-times-outline.svg'
import { ReactComponent as CalendarWarningOutline } from 'images/regulacao/calendar-warning-outline.svg'
import { Fragment } from 'react'
import { formatDateAndHoursMinutes, formatHoursMinutes } from 'util/date/formatDate'
import { humanizeDateTime } from 'util/date/humanize-date'
import StatusSquare from 'view/atendimentos/detail/historico/components/StatusSquare'

import {
  ListaRegulacaoFilterModel,
  ListaRegulacaoTableItem,
  regulacaoClassificacaoPrioridadeRecord,
  RegulacaoEnumTextMap,
  StatusRegulacaoEnumRecord,
} from '../model-regulacao'
import { ListaRegulacaoTableAction } from './ListaRegulacaoTableAction'
import { ListaRegulacaoTableFilter } from './ListaRegulacaoTableFilter'

interface ListaRegulacaoTableProps {
  data: ListaRegulacaoQuery
  loading: boolean
  filter: ListaRegulacaoFilterModel
  updateFilter(filter: ListaRegulacaoFilterModel): void
}

export const ListaRegulacaoTable = (props: ListaRegulacaoTableProps) => {
  const { data, loading, filter, updateFilter } = props

  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const tableProps = usePagedTableProps({
    loading,
    result: data.listaRegulacao,
    onChange: updateFilter,
  })

  return (
    <VFlow vSpacing={1} style={styles.line}>
      <Heading level={1} fontWeight='bold'>
        Solicitações em processo de regulação
      </Heading>
      {renderFiltrosVinculadosA(filter)}
      <ListaRegulacaoTableFilter filter={filter} updateFilter={updateFilter} />
      <TableBox>
        <DataTable<ListaRegulacaoTableItem>
          columns={[
            {
              name: 'statusGradient',
              render: renderStatusGradient,
              style: [styles.tableCell, styles.tableCellGradient],
            },
            {
              name: 'status',
              header: 'Status na regulação',
              render: (item) => renderStatus(item, now),
              style: [styles.tableCell, styles.tableCellStatus],
            },
            {
              name: 'prioridade',
              header: renderHeaderPrioridade,
              render: renderPrioridade,
              style: styles.tableCell,
            },
            {
              name: 'solicitacao',
              header: 'Solicitação',
              render: renderSolicitacao,
              style: styles.tableCell,
            },
            {
              name: 'cidadao',
              header: 'Cidadão',
              render: renderCidadao,
              style: [
                styles.tableCell,
                css`
                  width: 20%;
                `,
              ],
            },
            {
              name: 'solicitante',
              header: 'Solicitante',
              render: renderSolicitante,
              style: [
                styles.tableCell,
                css`
                  width: 20%;
                `,
              ],
            },
            {
              name: 'visualizar',
              header: '',
              render: (row) => <ListaRegulacaoTableAction item={row} />,
              style: styles.tableCell,
            },
          ]}
          {...tableProps}
        />
        <TableFooter
          {...tableProps}
          pageSize={tableProps.size}
          sizeOptions={[5, 10, 30, 50]}
          style={styles.tableFooter}
        />
      </TableBox>
    </VFlow>
  )
}

const renderStatusGradient = (item: ListaRegulacaoTableItem) => {
  const { colorScale } = StatusRegulacaoEnumMap[item.ultimaEvolucao.statusRegulacao]
  return <div css={styles.statusGradient(colorScale?.c30, colorScale?.c60)} />
}

const renderStatus = (item: ListaRegulacaoTableItem, now: Date) => {
  const dataEvolucao = item.ultimaEvolucao.dataEvolucao
  const isEvolucaoToday = isSameDay(toDate(dataEvolucao), now)
  const { icon: icone, svgIcon, colorScale } = StatusRegulacaoEnumMap[item.ultimaEvolucao.statusRegulacao]

  return (
    <VFlow vSpacing={0.125}>
      <Text>
        {isEvolucaoToday ? `Hoje às ${formatHoursMinutes(dataEvolucao)}` : formatDateAndHoursMinutes(dataEvolucao)}
      </Text>
      <HFlow hSpacing={0.5} alignItems='center'>
        {item.ultimaEvolucao.statusRegulacao !== StatusRegulacaoEnum.SOLICITADA &&
          (icone ? <Icon icon={icone} color={colorScale.c40} size={1} /> : svgIcon)}
        <Text>{RegulacaoEnumTextMap[item.ultimaEvolucao.statusRegulacao]}</Text>
      </HFlow>
    </VFlow>
  )
}

const renderHeaderPrioridade = (
  <HFlow hSpacing={0.25} alignItems='center'>
    <Text>Prioridade</Text>
    <InfoIcon icon='infoCircleFilled' text='Prioridade definida no processo de regulação' />
  </HFlow>
)

const renderPrioridade = (item: ListaRegulacaoTableItem) => {
  const prioridade = item.ultimaEvolucao.prioridade
  const { mainColor, label } = regulacaoClassificacaoPrioridadeRecord[prioridade]

  if (prioridade === PrioridadeRegulacaoEnum.NAO_INFORMADA) {
    return (
      <Fragment>
        <HideOnScreenSize down='lg'>
          <Text color='secondary' style={styles.noWrap}>
            {label}
          </Text>
        </HideOnScreenSize>
        <HideOnScreenSize up='lg'>
          <Text
            color='secondary'
            style={css`
              display: flex;
              text-align: center;
              white-space: nowrap;
            `}
          >
            {label}
          </Text>
        </HideOnScreenSize>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <HideOnScreenSize down='lg'>
        <StatusSquare color={mainColor} description={label} style={styles.noWrap} />
      </HideOnScreenSize>
      <HideOnScreenSize up='lg'>
        <HFlow justifyContent='center' alignItems='center'>
          <StatusSquare color={mainColor} description={label} descriptionAsTooltip />
        </HFlow>
      </HideOnScreenSize>
    </Fragment>
  )
}

const renderUbs = (nome: string) => (
  <Fragment>
    <HideOnScreenSize down='lg'>
      <HFlow hSpacing={0.2} alignItems='center'>
        <Icon icon='hospital' size={1} />
        <Ellipsis maxLines={1}>{nome}</Ellipsis>
      </HFlow>
    </HideOnScreenSize>
    <HideOnScreenSize up='lg'>
      <HFlow hSpacing={0.2} alignItems='center'>
        <Tooltip text={nome}>
          <Icon icon='hospital' size={1} />
        </Tooltip>
      </HFlow>
    </HideOnScreenSize>
  </Fragment>
)

const renderSolicitacao = (item: ListaRegulacaoTableItem) => {
  let text: string
  let date: LocalDate

  if (item.tipoRegulacao === TipoRegulacaoEnum.GUIA_ENCAMINHAMENTO) {
    const guia = item.guiaEncaminhamento
    text = guia.cbo.nome
    date = guia.atendimentoProfissional.finalizadoEm
  } else {
    const exame = item.exame.exame
    text = `${exame.descricao} - ${exame.codigo}`
    date = item.exame.atendimentoProfissionalSolicitacao.finalizadoEm
  }

  const ellipsesTitle = `${RegulacaoEnumTextMap[item.tipoRegulacao]} ${text}`
  const dateText = `Solicitado há ${humanizeDateTime(date)}`

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={3} title={ellipsesTitle}>
        <Text fontWeight='bold'>
          {item.tipoRegulacao === TipoRegulacaoEnum.SOLICITACAO_EXAME_OCI
            ? 'Procedimento '
            : RegulacaoEnumTextMap[item.tipoRegulacao]}{' '}
        </Text>
        <Text>{text}</Text>
      </Ellipsis>
      {date && (
        <Ellipsis maxLines={1} title={dateText}>
          <Text>{dateText}</Text>
        </Ellipsis>
      )}
    </VFlow>
  )
}

const renderCidadao = (item: ListaRegulacaoTableItem) => {
  const cidadao = item.prontuario.cidadao
  const vinculacaoEquipe = cidadao.cidadaoVinculacaoEquipe

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {(cidadao.nomeSocial ?? cidadao.nome).titleCase()}
      </Ellipsis>
      {vinculacaoEquipe?.equipe ? <Ellipsis maxLines={1}>{vinculacaoEquipe.equipe.nome}</Ellipsis> : 'Sem equipe'}
      {vinculacaoEquipe?.unidadeSaude && renderUbs(vinculacaoEquipe.unidadeSaude.nome)}
    </VFlow>
  )
}

const renderSolicitante = (item: ListaRegulacaoTableItem) => {
  const lotacao = item.lotacao

  return (
    <VFlow vSpacing={0}>
      <Ellipsis maxLines={1} fontWeight='bold'>
        {lotacao.profissional.nome}
      </Ellipsis>
      <Ellipsis maxLines={1}>{lotacao.cbo.nome}</Ellipsis>
      {lotacao.equipe && <Ellipsis maxLines={1}>{lotacao.equipe.nome}</Ellipsis>}
      {renderUbs(lotacao.unidadeSaude.nome)}
    </VFlow>
  )
}

const renderFiltrosVinculadosA = (filtros: ListaRegulacaoFilterModel) => {
  const {
    tableFilter: { equipe, unidadeSaude },
  } = filtros

  const filtroEquipe = equipe?.nome
  const filtroUbs = unidadeSaude?.nome

  return (
    <Heading level={4} fontWeight='normal'>
      {filtroEquipe || filtroUbs ? (
        <span>
          Cidadãos vinculados à <strong>{[filtroUbs, filtroEquipe].filterNotNull().join(' | ')}</strong>
        </span>
      ) : (
        <span>Todos os cidadãos do município</span>
      )}
    </Heading>
  )
}

const styles = {
  statusGradient: (from?: string, to?: string) => css`
    width: 0.5rem;
    height: 100%;
    ${from && to ? `background: linear-gradient(to top, ${from}, ${to});` : ''}
  `,
  tableCell: css`
    padding: 0.5rem 1rem;
  `,
  tableCellGradient: css`
    padding: 0;
    width: 0.5rem;
    &:is(td) {
      border-bottom: 0;
    }
  `,
  tableCellStatus: css`
    width: 12rem;
    padding-right: 3rem;
    padding-left: 0.5rem;
  `,
  tableFooter: css`
    border-top: 0;
  `,
  line: css`
    padding-top: 1rem;
    border-top: 1px solid ${colors.gray.c80};
  `,
  noWrap: css`
    white-space: nowrap;
  `,
  statusIcon: (color: ColorScale) => css`
    & path {
      fill: ${color.c40};
    }
    height: 1rem;
    width: 1rem;
  `,
  filledCircle: css`
    border-radius: 50%;
    background-color: ${colors.gray.c80};
  `,
}

export const StatusRegulacaoEnumMap: Record<StatusRegulacaoEnum, StatusRegulacaoEnumRecord> = {
  SOLICITADA: {
    svgIcon: <div css={styles.filledCircle} />,
  },
  PENDENTE_DE_AGENDAMENTO: {
    svgIcon: <CalendarClockOutline css={styles.statusIcon(colors.yellow)} />,
    colorScale: colors.yellow,
  },
  PENDENTE_DE_REGULACAO: {
    icon: 'clockOutline',
    colorScale: colors.yellow,
  },
  DEVOLVIDO_PELA_REGULACAO: {
    icon: 'userReturn',
    colorScale: colors.orange,
  },
  ATENDIMENTO_AGENDADO: {
    icon: 'calendarOutline',
    colorScale: colors.blue,
  },
  AGENDAMENTO_CONFIRMADO: {
    svgIcon: <CalendarCheckOutline css={styles.statusIcon(colors.green)} />,
    colorScale: colors.green,
  },
  AGENDAMENTO_COM_FALTA: {
    svgIcon: <CalendarWarningOutline css={styles.statusIcon(colors.purple)} />,
    colorScale: colors.purple,
  },
  SOLICITACAO_CANCELADA: {
    icon: 'trashOutline',
    colorScale: colors.gray,
  },
  AGENDAMENTO_CANCELADO: {
    svgIcon: <CalendarTimesOutline css={styles.statusIcon(colors.gray)} />,
    colorScale: colors.gray,
  },
  NEGADA_PELA_REGULACAO: {
    icon: 'banOutline',
    colorScale: colors.red,
  },
}
